
import { useState, useMemo } from 'react';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';

export interface PhoneCountry {
  code: string;
  name: string;
  dialCode: string;
  icon: string;
  length: number;
}

export interface UsePhoneInputOptions {
  defaultCountry?: string;
}

export const DEFAULT_PHONE_COUNTRIES: PhoneCountry[] = [
  { code: 'BR', name: 'Brazil', dialCode: '+55', icon: 'twemoji:flag-brazil', length: 10 },
  { code: 'CA', name: 'Canada', dialCode: '+1', icon: 'twemoji:flag-canada', length: 10 },
  { code: 'CN', name: 'China', dialCode: '+86', icon: 'twemoji:flag-china', length: 11 },
  { code: 'DE', name: 'Germany', dialCode: '+49', icon: 'twemoji:flag-germany', length: 10 },
  { code: 'ES', name: 'Spain', dialCode: '+34', icon: 'twemoji:flag-spain', length: 9 },
  { code: 'FR', name: 'France', dialCode: '+33', icon: 'twemoji:flag-france', length: 9 },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', icon: 'twemoji:flag-united-kingdom', length: 10 },
  { code: 'IT', name: 'Italy', dialCode: '+39', icon: 'twemoji:flag-italy', length: 10 },
  { code: 'JP', name: 'Japan', dialCode: '+81', icon: 'twemoji:flag-japan', length: 10 },
  { code: 'MX', name: 'Mexico', dialCode: '+52', icon: 'twemoji:flag-mexico', length: 10 },
  { code: 'US', name: 'United States', dialCode: '+1', icon: 'twemoji:flag-united-states', length: 10 }
];

export function usePhoneInput(options: UsePhoneInputOptions = {}) {
  const { defaultCountry = 'US' } = options;
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState<string | null>(null);

  const phoneCountries = useMemo(() => DEFAULT_PHONE_COUNTRIES, []);

  const phoneLengthMap = useMemo(() => {
    const map: Record<string, number> = {};
    phoneCountries.forEach(c => {
      map[c.code] = c.length;
    });
    return map;
  }, [phoneCountries]);

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setPhoneNumber('');
    setError(null);
  };

  const handlePhoneChange = (value: string) => {
    // 只保留数字
    const digits = value.replace(/\D/g, '');
    
    // 如果没有数字，清空
    if (!digits) {
      setPhoneNumber('');
      if (error) setError(null);
      return;
    }
    
    const expectedLength = phoneLengthMap[selectedCountry];

    // Format the phone number
    let formatted = digits;
    // if (expectedLength && digits.length >= expectedLength) {
      const parsedPhone = parsePhoneNumberFromString(digits, selectedCountry as any);
      if (parsedPhone && parsedPhone.isValid()) {
        const international = parsedPhone.formatInternational();
        const callingCode = parsedPhone.countryCallingCode;
        const prefix = `+${callingCode} `;
        const national = international.startsWith(prefix)
          ? international.slice(prefix.length)
          : international;
        formatted = national;
      }
    // } else {
    //   const formatter = new AsYouType(selectedCountry as any);
    //   formatted = formatter.input(digits);
    // }

    setPhoneNumber(formatted);
    if (error) setError(null);
  };

  const validatePhone = (value: string): boolean => {
    if (!value || !value.trim()) {
      setError('Phone number is required');
      return false;
    }

    const digits = value.replace(/\D/g, '');
    if (!/^\d+$/.test(digits)) {
      setError('Phone number must only contain digits');
      return false;
    }

    const expectedLength = phoneLengthMap[selectedCountry];
    if (expectedLength && digits.length !== expectedLength) {
      setError(`Phone number must be ${expectedLength} digits`);
      return false;
    }

    setError(null);
    return true;
  };

  const getFullPhoneNumber = () => {
    const country = phoneCountries.find(c => c.code === selectedCountry);
    if (!country) return phoneNumber;

    const digits = phoneNumber.replace(/\D/g, '');
    return `${country.dialCode}${digits}`;
  };

  const setFullPhoneNumber = (fullNumber: string) => {
    if (!fullNumber) return;
    
    // Try to parse with different countries to find a match
    // This is a heuristic since we don't know the country for sure without + prefix
    // But we assume the input might have the country code or not.
    
    // If it starts with +, use libphonenumber to parse
    if (fullNumber.startsWith('+')) {
      const parsed = parsePhoneNumberFromString(fullNumber);
      if (parsed && parsed.country) {
        setSelectedCountry(parsed.country);
        setPhoneNumber(parsed.formatNational());
        return;
      }
    }
    
    // If simply digits, we just set it as phone number and keep default country
    // unless we want to try to guess.
    setPhoneNumber(fullNumber);
  };

  return {
    selectedCountry,
    phoneNumber,
    error,
    phoneCountries,
    handleCountryChange,
    handlePhoneChange,
    validatePhone,
    getFullPhoneNumber,
    setPhoneNumber,
    setFullPhoneNumber,
    setError
  };
}
